import axios from 'axios'
import store from '@/store'
import router from '@/router'
import message from '@/utils/message'

// 基础配置：请根据环境自行调整
// 开发环境使用代理路径 /api，生产环境使用实际 API 地址
const baseURL = process.env.NODE_ENV === 'production'
  ? process.env.VUE_APP_BASE_API
  : '/api'

// /api/v1 类接口：开发与生产均直连环境变量中的完整地址，避免 devServer 代理与 /api 前缀冲突误转 m18787。
const baseURLV1 = String(
  process.env.VUE_APP_BASE_API_V1 || 'https://admin.ruisi-math.com/api/v1'
).replace(/\/$/, '')

function createService (url) {
  return axios.create({
    baseURL: url,
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json; charset=utf-8'
    }
  })
}

const service = createService(baseURL)
/** 与主业务不同前缀的接口（如 /api/v1/*） */

const serviceV1 = createService(baseURLV1)

// 用于存储所有正在进行的请求
const pendingRequests = new Map()

// 创建取消令牌的函数
const getCancelToken = () => {
  return axios.CancelToken.source()
}

// 清除所有正在进行的请求
const cancelAllRequests = (cancelMessage) => {
  pendingRequests.forEach((cancelToken, key) => {
    cancelToken.cancel(cancelMessage || '请求已取消')
    pendingRequests.delete(key)
  })
}

// 清除用户缓存和登录信息
const clearUserCache = () => {
  // 清除localStorage中的所有数据
  localStorage.clear()
  // 清除store中的用户信息
  if (store && store.dispatch) {
    store.dispatch('user/logout')
  }
}

// 处理登录失效的逻辑
const handleLoginExpired = (msg) => {
  // 取消所有正在进行的请求
  cancelAllRequests('登录失效，取消所有请求')

  // 清除缓存
  clearUserCache()

  // 显示提示信息
  message.error(msg)

  // 跳转到登录页面
  router.replace({
    path: '/login',
    query: {
      redirect: router.currentRoute.fullPath
    }
  })
}

function attachInterceptors (instance) {
  // 请求拦截：统一携带 Token
  instance.interceptors.request.use(
    config => {
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type']
      }
      const token = store?.getters?.['user/token'] || localStorage.getItem('token')
      if (token) {
        config.headers['user-token'] = token
      }

      // 为每个请求添加取消令牌
      const cancelToken = getCancelToken()
      config.cancelToken = cancelToken.token

      // 使用请求的url和method作为唯一标识
      const requestKey = `${config.method}_${config.url}`
      // 如果存在相同的请求，先取消之前的
      if (pendingRequests.has(requestKey)) {
        pendingRequests.get(requestKey).cancel('取消重复请求')
      }
      // 保存当前请求的取消函数
      pendingRequests.set(requestKey, cancelToken)

      return config
    },
    error => Promise.reject(error)
  )

  // 响应拦截：统一处理业务错误
  instance.interceptors.response.use(
    response => {
      const res = response.data || {}

      // 移除已完成的请求
      const requestKey = `${response.config.method}_${response.config.url}`
      pendingRequests.delete(requestKey)

      // 约定：后端以 code === 1 为成功
      if (res.code == 1) {
        return res
      }

      // 处理特定的错误码
      const code = res.code

      // 当返回code为-201, -202, -203时，跳转到登录页面并提示登录失效
      if (code == '-201' || code == '-202' || code == '-203') {
        handleLoginExpired('登录失效，请重新登录')
        return Promise.reject(new Error('登录失效，请重新登录'))
      }

      // 当返回code为401时，跳转到登录页面并提示未授权
      if (code == '401') {
        handleLoginExpired('未授权，请重新登录')
        return Promise.reject(new Error('未授权，请重新登录'))
      }

      // 其他错误码正常处理
      const errorMessage = res.msg || res.message || '请求失败'
      return Promise.reject(new Error(errorMessage))
    },
    error => {
      // 移除已完成的请求（包括被取消的）
      if (error.config) {
        const requestKey = `${error.config.method}_${error.config.url}`
        pendingRequests.delete(requestKey)
      }

      // 处理HTTP状态码401（未授权）
      if (error.response && error.response.status === 401) {
        handleLoginExpired('未授权，请重新登录')
        return Promise.reject(new Error('未授权，请重新登录'))
      }

      // 如果是取消请求的错误，不显示错误信息
      if (axios.isCancel(error)) {
        return Promise.reject(error)
      }

      // 其他错误正常处理
      return Promise.reject(error)
    }
  )
}

attachInterceptors(service)
attachInterceptors(serviceV1)

export default service
export { serviceV1 }
